import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BusinessLeadDetailModal } from '../components/business-leads/BusinessLeadDetailModal';
import {
  getBusinessTypeMapping,
  getBusinessTypeSuggestions,
  joinCommaValues,
  parseCommaValues,
} from '../lib/businessTypeTaxonomy';
import {
  Search,
  Save,
  Send,
  Loader2,
  RefreshCw,
  Building2,
  Database,
  Filter,
  FileDown,
  Plug,
  Plus,
  Trash2,
  Server,
  HardDrive,
  CloudDownload,
  Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultFilters = {
  query: '',
  source_scope: 'internal',
  sic_codes: '',
  naics_codes: '',
  states: '',
  cities: '',
  business_types: '',
  industries: '',
  employee_min: '',
  employee_max: '',
  revenue_min: '',
  revenue_max: '',
};

export default function AdminBusinessLeadsPage() {
  const [filters, setFilters] = useState(defaultFilters);
  const [businessTypeInput, setBusinessTypeInput] = useState('');
  const [businessTypeTokens, setBusinessTypeTokens] = useState([]);
  const [businessTypeSuggestions, setBusinessTypeSuggestions] = useState([]);
  const [customFilters, setCustomFilters] = useState([{ key: '', value: '' }]);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMode, setSearchMode] = useState('free');
  const [ensuringIndexes, setEnsuringIndexes] = useState(false);
  const [backfillLabelsRunning, setBackfillLabelsRunning] = useState(false);
  const [backfillLabelsJob, setBackfillLabelsJob] = useState(null);

  const [saving, setSaving] = useState(false);
  const [savedLeadsLoading, setSavedLeadsLoading] = useState(false);
  const [savedLeads, setSavedLeads] = useState([]);

  const [selectedSearchLeadIds, setSelectedSearchLeadIds] = useState(new Set());
  const [selectedSavedLeadIds, setSelectedSavedLeadIds] = useState(new Set());
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [leadDetailLoading, setLeadDetailLoading] = useState(false);
  const [leadDetailData, setLeadDetailData] = useState(null);
  const [leadDetailSourceLabel, setLeadDetailSourceLabel] = useState('search result');

  const [campaigns, setCampaigns] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [sending, setSending] = useState(false);

  const [providerLoading, setProviderLoading] = useState(false);
  const [providerSaving, setProviderSaving] = useState(false);
  const [providerSettings, setProviderSettings] = useState(null);

  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState([]);

  const [sendPayload, setSendPayload] = useState({
    campaign_id: '',
    cost_per_lead: '35',
    drip_daily_limit: '25',
    save_to_db: true,
    send_to_campaign_queue: true,
    export_csv: true,
  });

  const [ingestRunning, setIngestRunning] = useState(false);
  const [ingestJobsLoading, setIngestJobsLoading] = useState(false);
  const [ingestJobs, setIngestJobs] = useState([]);
  const [storageSummary, setStorageSummary] = useState(null);
  const [ingestForm, setIngestForm] = useState({
    source_sec_edgar: true,
    source_overpass_osm: true,
    source_opencorporates: false,
    storage_mode: 'hybrid',
    sec_limit: '15000',
    overpass_city_limit: '12',
    overpass_radius_km: '28',
    overpass_per_city_limit: '300',
    opencorporates_terms: 'roofing,hvac,legal,insurance,dental',
    opencorporates_pages: '2',
  });

  const [stateScrapeLoading, setStateScrapeLoading] = useState(false);
  const [stateScrapeSaving, setStateScrapeSaving] = useState(false);
  const [stateScrapeRunning, setStateScrapeRunning] = useState(false);
  const [stateScrapeConfig, setStateScrapeConfig] = useState({
    enabled: true,
    states_per_day: 5,
    target_per_state: 2000,
    daily_target: 10000,
    run_hour_utc: 3,
    run_minute_utc: 0,
    next_states_preview: [],
    last_run_date: null,
    next_state_index: 0,
  });

  const [importEntityName, setImportEntityName] = useState('custom_business_entity');
  const [importStorageMode, setImportStorageMode] = useState('hybrid');
  const [importFile, setImportFile] = useState(null);
  const [importSheetUrl, setImportSheetUrl] = useState('');
  const [importingFile, setImportingFile] = useState(false);
  const [importingSheet, setImportingSheet] = useState(false);
  const [entitySchemas, setEntitySchemas] = useState([]);

  const getHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const getSearchLeadKey = useCallback((lead) => (
    lead.external_business_id ||
    lead.id ||
    `${lead.company_name || 'unknown'}-${lead.website || 'n/a'}-${lead.city || 'n/a'}-${lead.state || 'n/a'}`
  ), []);

  const getMappedCodesFromTokens = useCallback((tokens) => {
    const sicSet = new Set();
    const naicsSet = new Set();
    tokens.forEach((token) => {
      const mapping = getBusinessTypeMapping(token);
      if (!mapping) return;
      (mapping.sic_codes || []).forEach((code) => sicSet.add(code));
      (mapping.naics_codes || []).forEach((code) => naicsSet.add(code));
    });
    return { sicSet, naicsSet };
  }, []);

  const applyBusinessTypeToken = useCallback((rawValue) => {
    const typedValue = String(rawValue || '').trim().toLowerCase();
    if (!typedValue) return;

    const suggestion = getBusinessTypeSuggestions(typedValue)[0];
    const mapping = getBusinessTypeMapping(typedValue) || suggestion || null;
    const tokenValue = mapping?.type || typedValue;

    if (businessTypeTokens.includes(tokenValue)) {
      setBusinessTypeInput('');
      setBusinessTypeSuggestions([]);
      return;
    }

    const nextTokens = [...businessTypeTokens, tokenValue];
    setBusinessTypeTokens(nextTokens);

    const existingSic = parseCommaValues(filters.sic_codes);
    const existingNaics = parseCommaValues(filters.naics_codes);
    const mergedSic = joinCommaValues([...existingSic, ...(mapping?.sic_codes || [])]);
    const mergedNaics = joinCommaValues([...existingNaics, ...(mapping?.naics_codes || [])]);

    setFilters((prev) => ({
      ...prev,
      business_types: joinCommaValues(nextTokens),
      sic_codes: mergedSic,
      naics_codes: mergedNaics,
    }));

    setBusinessTypeInput('');
    setBusinessTypeSuggestions([]);
  }, [businessTypeTokens, filters.naics_codes, filters.sic_codes]);

  const removeLastBusinessTypeToken = useCallback(() => {
    if (!businessTypeTokens.length) return;

    const removedToken = businessTypeTokens[businessTypeTokens.length - 1];
    const nextTokens = businessTypeTokens.slice(0, -1);
    const removedMapping = getBusinessTypeMapping(removedToken);
    const { sicSet: remainingSicSet, naicsSet: remainingNaicsSet } = getMappedCodesFromTokens(nextTokens);

    const nextSic = parseCommaValues(filters.sic_codes).filter((code) => {
      if (!(removedMapping?.sic_codes || []).includes(code)) return true;
      return remainingSicSet.has(code);
    });
    const nextNaics = parseCommaValues(filters.naics_codes).filter((code) => {
      if (!(removedMapping?.naics_codes || []).includes(code)) return true;
      return remainingNaicsSet.has(code);
    });

    setBusinessTypeTokens(nextTokens);
    setFilters((prev) => ({
      ...prev,
      business_types: joinCommaValues(nextTokens),
      sic_codes: joinCommaValues(nextSic),
      naics_codes: joinCommaValues(nextNaics),
    }));
  }, [businessTypeTokens, filters.naics_codes, filters.sic_codes, getMappedCodesFromTokens]);

  const parsedFilters = useMemo(() => {
    const custom_filter_obj = customFilters.reduce((acc, item) => {
      const key = item.key.trim();
      const value = item.value.trim();
      if (key && value) acc[key] = value;
      return acc;
    }, {});

    return {
      query: filters.query,
      source_scope: filters.source_scope,
      sic_codes: filters.sic_codes,
      naics_codes: filters.naics_codes,
      states: filters.states,
      cities: filters.cities,
      business_types: filters.business_types,
      industries: filters.industries,
      employee_min: filters.employee_min || undefined,
      employee_max: filters.employee_max || undefined,
      revenue_min: filters.revenue_min || undefined,
      revenue_max: filters.revenue_max || undefined,
      custom_filters: custom_filter_obj,
    };
  }, [filters, customFilters]);

  const fetchProviders = useCallback(async () => {
    try {
      setProviderLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/business-leads/providers`, {
        headers: getHeaders(),
      });
      setProviderSettings(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load provider settings');
    } finally {
      setProviderLoading(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/campaigns`, {
        headers: getHeaders(),
        params: { limit: 300 },
      });
      const businessCampaigns = (res.data || []).filter((c) => c.lead_type === 'business_leads');
      setCampaigns(businessCampaigns);
    } catch (error) {
      toast.error('Failed to load campaigns');
    }
  }, []);

  const fetchSavedLeads = useCallback(async () => {
    try {
      setSavedLeadsLoading(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/list`,
        {
          filters: { query: '' },
          limit: 150,
          skip: 0,
        },
        { headers: getHeaders() }
      );
      setSavedLeads(res.data.leads || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load saved business leads');
    } finally {
      setSavedLeadsLoading(false);
    }
  }, []);

  const fetchDeliveryLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-leads/delivery-logs`, {
        headers: getHeaders(),
        params: { limit: 150 },
      });
      setDeliveryLogs(res.data.logs || []);
    } catch (error) {
      toast.error('Failed to load delivery logs');
    }
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-leads/filter-presets`, {
        headers: getHeaders(),
      });
      setPresets(res.data.presets || []);
    } catch (error) {
      toast.error('Failed to load saved filters');
    }
  }, []);

  const fetchIngestJobs = useCallback(async () => {
    try {
      setIngestJobsLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/business-leads/ingest/jobs`, {
        headers: getHeaders(),
        params: { limit: 40 },
      });
      setIngestJobs(res.data.jobs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load ingestion jobs');
    } finally {
      setIngestJobsLoading(false);
    }
  }, []);

  const fetchStorageSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-leads/storage/summary`, {
        headers: getHeaders(),
      });
      setStorageSummary(res.data || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load storage summary');
    }
  }, []);

  const fetchStateScrapeConfig = useCallback(async () => {
    try {
      setStateScrapeLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/business-leads/state-scrape/config`, {
        headers: getHeaders(),
      });
      setStateScrapeConfig((prev) => ({ ...prev, ...(res.data || {}) }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load state scrape config');
    } finally {
      setStateScrapeLoading(false);
    }
  }, []);

  const fetchEntitySchemas = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-leads/import/entities`, {
        headers: getHeaders(),
      });
      setEntitySchemas(res.data.entities || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load entity schemas');
    }
  }, []);

  const openLeadDetail = async (lead, sourceType) => {
    if (!lead) return;

    setLeadDetailOpen(true);
    setLeadDetailSourceLabel(sourceType === 'saved' ? 'saved business lead' : 'search result');
    setLeadDetailData(lead);

    if (sourceType !== 'saved' || !lead.id) return;

    try {
      setLeadDetailLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/business-leads/lead/${lead.id}`, {
        headers: getHeaders(),
      });
      setLeadDetailData(res.data?.lead || lead);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load full lead detail');
    } finally {
      setLeadDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchCampaigns();
    fetchSavedLeads();
    fetchDeliveryLogs();
    fetchPresets();
    fetchIngestJobs();
    fetchStorageSummary();
    fetchStateScrapeConfig();
    fetchEntitySchemas();
  }, [
    fetchProviders,
    fetchCampaigns,
    fetchSavedLeads,
    fetchDeliveryLogs,
    fetchPresets,
    fetchIngestJobs,
    fetchStorageSummary,
    fetchStateScrapeConfig,
    fetchEntitySchemas,
  ]);

  const runSearch = async () => {
    try {
      setSearching(true);

      const pendingInput = String(businessTypeInput || '').trim().toLowerCase();
      let effectiveTokens = [...businessTypeTokens];
      if (pendingInput && !effectiveTokens.includes(pendingInput)) {
        effectiveTokens = [...effectiveTokens, pendingInput];
      }

      let effectiveFilters = { ...parsedFilters };
      if (pendingInput) {
        const normalizedTokenString = joinCommaValues(effectiveTokens);
        const mappedCodes = getMappedCodesFromTokens(effectiveTokens);

        effectiveFilters = {
          ...effectiveFilters,
          business_types: normalizedTokenString,
          sic_codes: mappedCodes.sicSet.size ? joinCommaValues(Array.from(mappedCodes.sicSet)) : effectiveFilters.sic_codes,
          naics_codes: mappedCodes.naicsSet.size ? joinCommaValues(Array.from(mappedCodes.naicsSet)) : effectiveFilters.naics_codes,
        };

        setBusinessTypeTokens(effectiveTokens);
        setBusinessTypeInput('');
        setBusinessTypeSuggestions([]);
        setFilters((prev) => ({
          ...prev,
          business_types: effectiveFilters.business_types,
          sic_codes: effectiveFilters.sic_codes,
          naics_codes: effectiveFilters.naics_codes,
        }));
      }

      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/search`,
        {
          filters: effectiveFilters,
          limit: 120,
        },
        { headers: getHeaders() }
      );
      setSearchResults(res.data.results || []);
      setSearchMode(res.data.mode || 'free');
      setSelectedSearchLeadIds(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const ensureSearchIndexes = async () => {
    try {
      setEnsuringIndexes(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/indexes/ensure`,
        {},
        { headers: getHeaders() }
      );
      const created = (res.data?.created || []).length;
      toast.success(`Search indexes ready (${created} ensured)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ensure search indexes');
    } finally {
      setEnsuringIndexes(false);
    }
  };

  const runBackfillLabels = async () => {
    try {
      setBackfillLabelsRunning(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/backfill-labels`,
        { limit: 250000 },
        { headers: getHeaders() }
      );
      const job = res.data?.job || null;
      setBackfillLabelsJob(job);
      toast.success(`Backfill queued: ${job?.id || 'job created'}`);

      if (job?.id) {
        let done = false;
        for (let i = 0; i < 25; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          const statusRes = await axios.get(`${API_URL}/api/admin/business-leads/backfill-labels/${job.id}`, {
            headers: getHeaders(),
          });
          const statusJob = statusRes.data?.job;
          setBackfillLabelsJob(statusJob || null);
          if (statusJob?.status === 'completed') {
            const updated = statusJob?.result?.updated || 0;
            toast.success(`Backfill complete. Updated ${updated} lead labels.`);
            done = true;
            break;
          }
          if (statusJob?.status === 'failed') {
            toast.error(statusJob?.error || 'Label backfill failed');
            done = true;
            break;
          }
        }
        if (!done) {
          toast.success('Backfill is still running in background.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue label backfill');
    } finally {
      setBackfillLabelsRunning(false);
      fetchSavedLeads();
    }
  };

  const allSearchSelected = useMemo(() => (
    searchResults.length > 0 &&
    searchResults.every((lead) => selectedSearchLeadIds.has(getSearchLeadKey(lead)))
  ), [getSearchLeadKey, searchResults, selectedSearchLeadIds]);

  const someSearchSelected = useMemo(() => (
    searchResults.some((lead) => selectedSearchLeadIds.has(getSearchLeadKey(lead)))
  ), [getSearchLeadKey, searchResults, selectedSearchLeadIds]);

  const toggleAllSearchLeads = (checked) => {
    if (!checked) {
      setSelectedSearchLeadIds(new Set());
      return;
    }
    const allIds = searchResults.map((lead) => getSearchLeadKey(lead));
    setSelectedSearchLeadIds(new Set(allIds));
  };

  const saveSelectedSearchLeads = async () => {
    const selected = searchResults.filter((lead) => selectedSearchLeadIds.has(getSearchLeadKey(lead)));
    if (!selected.length) {
      toast.error('Select at least one search result');
      return;
    }

    try {
      setSaving(true);
      const normalized = selected.map((lead) => ({
        id: lead.id,
        external_business_id: lead.external_business_id,
        company_name: lead.company_name,
        contact_name: lead.contact_name,
        email: lead.email,
        phone: lead.phone,
        website: lead.website,
        sic_code: lead.sic_code,
        naics_code: lead.naics_code,
        business_type: lead.business_type,
        industry: lead.industry,
        state: lead.state,
        city: lead.city,
        zip_code: lead.zip_code,
        employee_count: lead.employee_count,
        annual_revenue: lead.annual_revenue,
        source_provider: lead.source_provider,
        source_mode: lead.source_mode,
        custom_fields: lead.custom_fields || {},
      }));

      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/save`,
        { leads: normalized },
        { headers: getHeaders() }
      );

      toast.success(`Saved ${res.data.saved} leads (${res.data.inserted} new, ${res.data.updated} updated)`);
      fetchSavedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save business leads');
    } finally {
      setSaving(false);
    }
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Preset name is required');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/business-leads/filter-presets`,
        { name: presetName.trim(), filters: parsedFilters },
        { headers: getHeaders() }
      );
      setPresetName('');
      fetchPresets();
      toast.success('Filter preset saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preset');
    }
  };

  const applyPreset = (preset) => {
    const p = preset.filters || {};
    const presetBusinessTypes = parseCommaValues(p.business_types || '');
    setFilters((prev) => ({
      ...prev,
      query: p.query || '',
      source_scope: p.source_scope || 'internal',
      sic_codes: p.sic_codes || '',
      naics_codes: p.naics_codes || '',
      states: p.states || '',
      cities: p.cities || '',
      business_types: joinCommaValues(presetBusinessTypes),
      industries: p.industries || '',
      employee_min: p.employee_min || '',
      employee_max: p.employee_max || '',
      revenue_min: p.revenue_min || '',
      revenue_max: p.revenue_max || '',
    }));
    setBusinessTypeTokens(presetBusinessTypes);
    setBusinessTypeInput('');
    setBusinessTypeSuggestions([]);
    const customObj = p.custom_filters || {};
    const customRows = Object.entries(customObj).map(([key, value]) => ({ key, value: String(value) }));
    setCustomFilters(customRows.length ? customRows : [{ key: '', value: '' }]);
    toast.success(`Applied preset: ${preset.name}`);
  };

  const updateProviderSettings = async () => {
    if (!providerSettings) return;
    try {
      setProviderSaving(true);
      const payload = {
        free_mode_enabled: providerSettings.free_mode_enabled,
        providers: {
          clearbit: {
            enabled: providerSettings.providers?.clearbit?.enabled,
            api_key: providerSettings.providers?.clearbit?.api_key || undefined,
          },
          zoominfo: {
            enabled: providerSettings.providers?.zoominfo?.enabled,
            api_key: providerSettings.providers?.zoominfo?.api_key || undefined,
            username: providerSettings.providers?.zoominfo?.username || undefined,
            password: providerSettings.providers?.zoominfo?.password || undefined,
          },
        },
      };

      const res = await axios.put(`${API_URL}/api/admin/business-leads/providers`, payload, {
        headers: getHeaders(),
      });

      setProviderSettings((prev) => ({
        ...prev,
        ...(res.data.settings || {}),
      }));
      toast.success('Provider settings updated');
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update provider settings');
    } finally {
      setProviderSaving(false);
    }
  };

  const runIngestion = async () => {
    const sources = [];
    if (ingestForm.source_sec_edgar) sources.push('sec_edgar');
    if (ingestForm.source_overpass_osm) sources.push('overpass_osm');
    if (ingestForm.source_opencorporates) sources.push('opencorporates');

    if (!sources.length) {
      toast.error('Select at least one source');
      return;
    }

    try {
      setIngestRunning(true);
      const payload = {
        sources,
        storage_mode: ingestForm.storage_mode,
        options: {
          sec_limit: Number(ingestForm.sec_limit || 15000),
          overpass_city_limit: Number(ingestForm.overpass_city_limit || 12),
          overpass_radius_km: Number(ingestForm.overpass_radius_km || 28),
          overpass_per_city_limit: Number(ingestForm.overpass_per_city_limit || 300),
          opencorporates_terms: ingestForm.opencorporates_terms,
          opencorporates_pages: Number(ingestForm.opencorporates_pages || 2),
        },
      };

      const res = await axios.post(`${API_URL}/api/admin/business-leads/ingest/run`, payload, {
        headers: getHeaders(),
      });
      toast.success(`Ingestion started: ${res.data?.job?.id || 'job created'}`);
      fetchIngestJobs();

      // quick refresh loop so status updates without manual refresh
      setTimeout(() => fetchIngestJobs(), 3000);
      setTimeout(() => fetchIngestJobs(), 7000);
      setTimeout(() => {
        fetchIngestJobs();
        fetchStorageSummary();
      }, 12000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start ingestion');
    } finally {
      setIngestRunning(false);
    }
  };

  const saveStateScrapeConfig = async () => {
    try {
      setStateScrapeSaving(true);
      const payload = {
        enabled: stateScrapeConfig.enabled,
        states_per_day: Number(stateScrapeConfig.states_per_day || 5),
        target_per_state: Number(stateScrapeConfig.target_per_state || 2000),
        daily_target: Number(stateScrapeConfig.daily_target || 10000),
        run_hour_utc: Number(stateScrapeConfig.run_hour_utc || 3),
        run_minute_utc: Number(stateScrapeConfig.run_minute_utc || 0),
      };
      const res = await axios.put(`${API_URL}/api/admin/business-leads/state-scrape/config`, payload, {
        headers: getHeaders(),
      });
      setStateScrapeConfig((prev) => ({ ...prev, ...(res.data?.config || {}) }));
      toast.success('State scrape config saved');
      fetchStateScrapeConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save state scrape config');
    } finally {
      setStateScrapeSaving(false);
    }
  };

  const runStateDailyBatchNow = async () => {
    try {
      setStateScrapeRunning(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/state-scrape/run-daily`,
        { force: true },
        { headers: getHeaders() }
      );
      toast.success(`State batch started for: ${(res.data?.selected_states || []).join(', ')}`);
      fetchIngestJobs();
      fetchStorageSummary();
      fetchStateScrapeConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run state daily batch');
    } finally {
      setStateScrapeRunning(false);
    }
  };

  const importFromFile = async () => {
    if (!importFile) {
      toast.error('Select a file first');
      return;
    }
    if (!importEntityName.trim()) {
      toast.error('Entity name is required');
      return;
    }

    try {
      setImportingFile(true);
      const form = new FormData();
      form.append('file', importFile);
      form.append('entity_name', importEntityName.trim());
      form.append('storage_mode', importStorageMode);

      const res = await axios.post(`${API_URL}/api/admin/business-leads/import/file`, form, {
        headers: {
          ...getHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`Imported ${res.data.rows_processed} rows (${res.data.inserted} inserted, ${res.data.updated} updated)`);
      fetchSavedLeads();
      fetchStorageSummary();
      fetchEntitySchemas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'File import failed');
    } finally {
      setImportingFile(false);
    }
  };

  const importFromGoogleSheet = async () => {
    if (!importSheetUrl.trim()) {
      toast.error('Google Sheets URL is required');
      return;
    }
    if (!importEntityName.trim()) {
      toast.error('Entity name is required');
      return;
    }

    try {
      setImportingSheet(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/import/google-sheet`,
        {
          sheet_url: importSheetUrl.trim(),
          entity_name: importEntityName.trim(),
          storage_mode: importStorageMode,
        },
        { headers: getHeaders() }
      );
      toast.success(`Imported ${res.data.rows_processed} rows from Sheets (${res.data.inserted} inserted, ${res.data.updated} updated)`);
      fetchSavedLeads();
      fetchStorageSummary();
      fetchEntitySchemas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Sheets import failed');
    } finally {
      setImportingSheet(false);
    }
  };

  const sendSelectedToCampaign = async () => {
    if (!sendPayload.campaign_id) {
      toast.error('Select a business campaign first');
      return;
    }
    const leadIds = Array.from(selectedSavedLeadIds);
    if (!leadIds.length) {
      toast.error('Select at least one saved business lead');
      return;
    }

    try {
      setSending(true);
      const res = await axios.post(
        `${API_URL}/api/admin/business-leads/send`,
        {
          lead_ids: leadIds,
          campaign_id: sendPayload.campaign_id,
          source_type: 'business_database',
          filters_snapshot: parsedFilters,
          cost_per_lead: Number(sendPayload.cost_per_lead || 0),
          drip_daily_limit: Number(sendPayload.drip_daily_limit || 0),
          actions: {
            save_to_db: sendPayload.save_to_db,
            send_to_campaign_queue: sendPayload.send_to_campaign_queue,
            export_csv: sendPayload.export_csv,
          },
        },
        { headers: getHeaders() }
      );

      const summary = res.data.summary || {};
      toast.success(`Sent request processed. queued=${summary.queued || 0}, duplicates=${summary.duplicates_skipped || 0}`);

      if (res.data.csv_content) {
        const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `business_leads_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      fetchDeliveryLogs();
      fetchSavedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send business leads');
    } finally {
      setSending(false);
    }
  };

  const toggleSearchLead = (externalId, checked) => {
    setSelectedSearchLeadIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(externalId);
      else next.delete(externalId);
      return next;
    });
  };

  const toggleSavedLead = (leadId, checked) => {
    setSelectedSavedLeadIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-business-leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-testid="business-leads-title">Business Leads Database</h1>
          <p className="text-muted-foreground" data-testid="business-leads-description">
            Search by SIC/NAICS/location/type, save, filter, and send to business campaigns with duplicate control.
          </p>
        </div>
        <Badge variant="outline" data-testid="business-leads-mode-badge">
          Mode: {searchMode}
        </Badge>
      </div>

      <Tabs defaultValue="search" data-testid="business-leads-tabs">
        <TabsList>
          <TabsTrigger value="search" data-testid="business-tab-search">Search & Save</TabsTrigger>
          <TabsTrigger value="send" data-testid="business-tab-send">Send & Logs</TabsTrigger>
          <TabsTrigger value="ingestion" data-testid="business-tab-ingestion">Ingestion</TabsTrigger>
          <TabsTrigger value="providers" data-testid="business-tab-providers">Providers</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Filter className="w-4 h-4" /> Search Filters</CardTitle>
              <CardDescription>All filters support comma-separated values and custom fields.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Label>Business Types</Label>
                  <Input
                    data-testid="business-filter-types-input"
                    value={businessTypeInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBusinessTypeInput(value);
                      setBusinessTypeSuggestions(getBusinessTypeSuggestions(value).slice(0, 8));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyBusinessTypeToken(businessTypeInput);
                      }
                      if (e.key === 'Backspace' && !businessTypeInput.trim()) {
                        removeLastBusinessTypeToken();
                      }
                    }}
                    placeholder="Type business (ex: roofing), press Enter"
                  />

                  {businessTypeSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border bg-white shadow" data-testid="business-type-suggestions-list">
                      {businessTypeSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.type}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                          data-testid={`business-type-suggestion-${suggestion.type}`}
                          onClick={() => applyBusinessTypeToken(suggestion.type)}
                        >
                          {suggestion.type} <span className="text-xs text-slate-500">SIC {suggestion.sic_codes.join('/')} • NAICS {suggestion.naics_codes.join('/')}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2" data-testid="business-type-token-container">
                    {businessTypeTokens.map((token) => (
                      <Badge key={token} variant="secondary" data-testid={`business-type-token-${token}`}>
                        {token}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Search Source</Label>
                  <Select
                    value={filters.source_scope || 'hybrid'}
                    onValueChange={(value) => setFilters((p) => ({ ...p, source_scope: value }))}
                  >
                    <SelectTrigger data-testid="business-filter-source-scope">
                      <SelectValue placeholder="Choose source scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal DB only</SelectItem>
                      <SelectItem value="external">External only</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Internal + External)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>SIC Codes</Label>
                  <Input data-testid="business-filter-sic" value={filters.sic_codes} onChange={(e) => setFilters((p) => ({ ...p, sic_codes: e.target.value }))} placeholder="1711, 8111" />
                </div>
                <div>
                  <Label>NAICS Codes</Label>
                  <Input data-testid="business-filter-naics" value={filters.naics_codes} onChange={(e) => setFilters((p) => ({ ...p, naics_codes: e.target.value }))} placeholder="238220, 541110" />
                </div>
                <div>
                  <Label>States</Label>
                  <Input data-testid="business-filter-states" value={filters.states} onChange={(e) => setFilters((p) => ({ ...p, states: e.target.value }))} placeholder="CA, TX, FL" />
                </div>
                <div>
                  <Label>Cities</Label>
                  <Input data-testid="business-filter-cities" value={filters.cities} onChange={(e) => setFilters((p) => ({ ...p, cities: e.target.value }))} placeholder="Los Angeles, Dallas" />
                </div>
                <div>
                  <Label>Industries</Label>
                  <Input data-testid="business-filter-industries" value={filters.industries} onChange={(e) => setFilters((p) => ({ ...p, industries: e.target.value }))} placeholder="HVAC Services, Legal Services" />
                </div>
                <div>
                  <Label>Employee Min</Label>
                  <Input type="number" data-testid="business-filter-employee-min" value={filters.employee_min} onChange={(e) => setFilters((p) => ({ ...p, employee_min: e.target.value }))} />
                </div>
                <div>
                  <Label>Employee Max</Label>
                  <Input type="number" data-testid="business-filter-employee-max" value={filters.employee_max} onChange={(e) => setFilters((p) => ({ ...p, employee_max: e.target.value }))} />
                </div>
                <div>
                  <Label>Revenue Min</Label>
                  <Input type="number" data-testid="business-filter-revenue-min" value={filters.revenue_min} onChange={(e) => setFilters((p) => ({ ...p, revenue_min: e.target.value }))} />
                </div>
                <div>
                  <Label>Revenue Max</Label>
                  <Input type="number" data-testid="business-filter-revenue-max" value={filters.revenue_max} onChange={(e) => setFilters((p) => ({ ...p, revenue_max: e.target.value }))} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Custom Filters</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="business-add-custom-filter"
                    onClick={() => setCustomFilters((prev) => [...prev, { key: '', value: '' }])}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Custom
                  </Button>
                </div>
                {customFilters.map((row, idx) => (
                  <div key={`custom-${idx}`} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <Input
                      data-testid={`business-custom-key-${idx}`}
                      placeholder="field"
                      value={row.key}
                      onChange={(e) => setCustomFilters((prev) => prev.map((item, i) => (i === idx ? { ...item, key: e.target.value } : item)))}
                    />
                    <Input
                      data-testid={`business-custom-value-${idx}`}
                      className="md:col-span-3"
                      placeholder="value"
                      value={row.value}
                      onChange={(e) => setCustomFilters((prev) => prev.map((item, i) => (i === idx ? { ...item, value: e.target.value } : item)))}
                    />
                    <Button
                      variant="ghost"
                      data-testid={`business-remove-custom-${idx}`}
                      onClick={() => setCustomFilters((prev) => prev.length === 1 ? [{ key: '', value: '' }] : prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button data-testid="business-search-button" onClick={runSearch} disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />} Search
                </Button>
                <Button data-testid="business-refresh-saved-button" variant="outline" onClick={fetchSavedLeads}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh Saved
                </Button>
                <Input
                  data-testid="business-preset-name"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Save filter preset name"
                  className="max-w-xs"
                />
                <Button data-testid="business-save-preset-button" variant="outline" onClick={savePreset}>
                  <Save className="w-4 h-4 mr-2" /> Save Preset
                </Button>
                <Button
                  data-testid="business-ensure-indexes-button"
                  variant="outline"
                  onClick={ensureSearchIndexes}
                  disabled={ensuringIndexes}
                >
                  {ensuringIndexes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                  Optimize Search Indexes
                </Button>
                <Button
                  data-testid="business-backfill-labels-button"
                  variant="outline"
                  onClick={runBackfillLabels}
                  disabled={backfillLabelsRunning}
                >
                  {backfillLabelsRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Backfill Industry/Type Labels
                </Button>
              </div>

              {backfillLabelsJob && (
                <div className="text-sm text-muted-foreground" data-testid="business-backfill-labels-job-status">
                  Backfill job: {backfillLabelsJob.id} • status: {backfillLabelsJob.status}
                  {backfillLabelsJob?.result ? ` • updated: ${backfillLabelsJob.result.updated || 0}` : ''}
                </div>
              )}

              {presets.length > 0 && (
                <div className="space-y-2" data-testid="business-presets-list">
                  <Label>Saved Presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {presets.map((preset) => (
                      <Button key={preset.id} variant="secondary" size="sm" data-testid={`business-apply-preset-${preset.id}`} onClick={() => applyPreset(preset)}>
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Search className="w-4 h-4" /> Search Results</CardTitle>
              <CardDescription data-testid="business-search-result-count">{searchResults.length} result(s)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button data-testid="business-save-selected-results" onClick={saveSelectedSearchLeads} disabled={saving || selectedSearchLeadIds.size === 0}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />} Save Selected ({selectedSearchLeadIds.size})
                </Button>
                <Button
                  variant="outline"
                  data-testid="business-select-search-all-button"
                  onClick={() => toggleAllSearchLeads(!allSearchSelected)}
                  disabled={searchResults.length === 0}
                >
                  {allSearchSelected ? 'Clear All' : 'Select All Results'}
                </Button>
              </div>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            data-testid="business-select-search-all-checkbox"
                            checked={allSearchSelected ? true : (someSearchSelected ? 'indeterminate' : false)}
                            onCheckedChange={(checked) => toggleAllSearchLeads(checked === true)}
                          />
                          <span>Select</span>
                        </div>
                      </TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>SIC/NAICS</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Employees</TableHead>
                      <TableHead>Lead Score</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">No results yet.</TableCell>
                      </TableRow>
                    ) : searchResults.map((lead) => {
                      const leadKey = getSearchLeadKey(lead);
                      return (
                      <TableRow
                        key={leadKey}
                        className="cursor-pointer hover:bg-muted/40"
                        data-testid={`business-search-row-${leadKey}`}
                        onClick={() => openLeadDetail(lead, 'search')}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            data-testid={`business-select-search-${leadKey}`}
                            checked={selectedSearchLeadIds.has(leadKey)}
                            onCheckedChange={(checked) => toggleSearchLead(leadKey, checked === true)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2" data-testid={`business-search-company-${leadKey}`}>
                            {lead.company_name}
                            <Eye className="w-3 h-3 text-muted-foreground" />
                          </div>
                          <div className="text-xs text-muted-foreground">{lead.website || '-'}</div>
                        </TableCell>
                        <TableCell>{lead.business_type || '-'}</TableCell>
                        <TableCell>{lead.sic_code || '-'} / {lead.naics_code || '-'}</TableCell>
                        <TableCell>{lead.city || '-'}, {lead.state || '-'}</TableCell>
                        <TableCell>{lead.employee_count ?? '-'}</TableCell>
                        <TableCell data-testid={`business-search-score-${leadKey}`}>{lead.lead_score ?? '-'}</TableCell>
                        <TableCell><Badge variant="outline" data-testid={`business-search-source-${leadKey}`}>{lead.source_provider || 'free'}</Badge></TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="send" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Send className="w-4 h-4" /> Send to Business Campaign</CardTitle>
              <CardDescription>Duplicate protection is enforced per campaign.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Business Campaign</Label>
                  <Select value={sendPayload.campaign_id} onValueChange={(value) => setSendPayload((prev) => ({ ...prev, campaign_id: value }))}>
                    <SelectTrigger data-testid="business-send-campaign-select">
                      <SelectValue placeholder="Select campaign" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cost per Lead ($)</Label>
                  <Input type="number" data-testid="business-send-cpl" value={sendPayload.cost_per_lead} onChange={(e) => setSendPayload((p) => ({ ...p, cost_per_lead: e.target.value }))} />
                </div>
                <div>
                  <Label>Drip Daily Limit</Label>
                  <Input type="number" data-testid="business-send-drip-limit" value={sendPayload.drip_daily_limit} onChange={(e) => setSendPayload((p) => ({ ...p, drip_daily_limit: e.target.value }))} />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox data-testid="business-action-save" checked={sendPayload.save_to_db} onCheckedChange={(checked) => setSendPayload((p) => ({ ...p, save_to_db: checked === true }))} />
                  <Label>Save to DB</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox data-testid="business-action-queue" checked={sendPayload.send_to_campaign_queue} onCheckedChange={(checked) => setSendPayload((p) => ({ ...p, send_to_campaign_queue: checked === true }))} />
                  <Label>Push to Campaign Queue</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox data-testid="business-action-csv" checked={sendPayload.export_csv} onCheckedChange={(checked) => setSendPayload((p) => ({ ...p, export_csv: checked === true }))} />
                  <Label>Export CSV</Label>
                </div>
              </div>

              <Button data-testid="business-send-selected-button" onClick={sendSelectedToCampaign} disabled={sending || selectedSavedLeadIds.size === 0}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send Selected ({selectedSavedLeadIds.size})
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="w-4 h-4" /> Saved Business Leads</CardTitle>
              <CardDescription data-testid="business-saved-count">{savedLeads.length} saved lead(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {savedLeadsLoading ? (
                <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Select</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>SIC/NAICS</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Lead Score</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {savedLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">No saved business leads.</TableCell>
                        </TableRow>
                      ) : savedLeads.map((lead) => (
                        <TableRow
                          key={lead.id}
                          className="cursor-pointer hover:bg-muted/40"
                          data-testid={`business-saved-row-${lead.id}`}
                          onClick={() => openLeadDetail(lead, 'saved')}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              data-testid={`business-select-saved-${lead.id}`}
                              checked={selectedSavedLeadIds.has(lead.id)}
                              onCheckedChange={(checked) => toggleSavedLead(lead.id, checked === true)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium flex items-center gap-2" data-testid={`business-saved-company-${lead.id}`}>
                              {lead.company_name}
                              <Eye className="w-3 h-3 text-muted-foreground" />
                              {lead.verification_failed && <Badge variant="destructive" data-testid={`business-saved-verification-failed-${lead.id}`}>Verify</Badge>}
                            </div>
                            <div className="text-xs text-muted-foreground">{lead.website || '-'}</div>
                          </TableCell>
                          <TableCell>{lead.business_type || '-'}</TableCell>
                          <TableCell>{lead.sic_code || '-'} / {lead.naics_code || '-'}</TableCell>
                          <TableCell>{lead.city || '-'}, {lead.state || '-'}</TableCell>
                          <TableCell data-testid={`business-saved-score-${lead.id}`}>{lead.lead_score ?? '-'}</TableCell>
                          <TableCell><Badge variant="outline">{lead.source_provider || 'unknown'}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileDown className="w-4 h-4" /> Delivery Logs</CardTitle>
              <CardDescription>Track what was sent, queued, and duplicate-skipped by campaign.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Button variant="outline" data-testid="business-refresh-logs-button" onClick={fetchDeliveryLogs}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh Logs
                </Button>
              </div>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Queued</TableHead>
                      <TableHead>Released</TableHead>
                      <TableHead>Duplicates</TableHead>
                      <TableHead>Cost/Lead</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveryLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">No delivery logs yet.</TableCell>
                      </TableRow>
                    ) : deliveryLogs.map((log) => (
                      <TableRow key={log.id} data-testid={`business-delivery-log-${log.id}`}>
                        <TableCell>{log.campaign_name}</TableCell>
                        <TableCell>{log.requested_lead_count}</TableCell>
                        <TableCell>{log.queued_count}</TableCell>
                        <TableCell>{log.released_now}</TableCell>
                        <TableCell>{log.duplicate_skipped}</TableCell>
                        <TableCell>${Number(log.cost_per_lead || 0).toFixed(2)}</TableCell>
                        <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ingestion" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Server className="w-4 h-4" /> Free Source Ingestion</CardTitle>
              <CardDescription>
                Build your own long-term business database. Recommended mode: <strong>hybrid</strong> (searchable DB + compressed raw snapshots).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="ingest-source-sec-edgar"
                    checked={ingestForm.source_sec_edgar}
                    onCheckedChange={(checked) => setIngestForm((p) => ({ ...p, source_sec_edgar: checked === true }))}
                  />
                  <Label>SEC EDGAR (free)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="ingest-source-overpass"
                    checked={ingestForm.source_overpass_osm}
                    onCheckedChange={(checked) => setIngestForm((p) => ({ ...p, source_overpass_osm: checked === true }))}
                  />
                  <Label>Overpass OSM (free)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="ingest-source-opencorporates"
                    checked={ingestForm.source_opencorporates}
                    onCheckedChange={(checked) => setIngestForm((p) => ({ ...p, source_opencorporates: checked === true }))}
                  />
                  <Label>OpenCorporates</Label>
                </div>
                <div>
                  <Label>Storage Mode</Label>
                  <Select value={ingestForm.storage_mode} onValueChange={(value) => setIngestForm((p) => ({ ...p, storage_mode: value }))}>
                    <SelectTrigger data-testid="ingest-storage-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hybrid">Hybrid (DB + folder snapshots)</SelectItem>
                      <SelectItem value="database_only">Database Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>SEC limit</Label>
                  <Input data-testid="ingest-sec-limit" type="number" value={ingestForm.sec_limit} onChange={(e) => setIngestForm((p) => ({ ...p, sec_limit: e.target.value }))} />
                </div>
                <div>
                  <Label>OSM city seeds</Label>
                  <Input data-testid="ingest-overpass-city-limit" type="number" value={ingestForm.overpass_city_limit} onChange={(e) => setIngestForm((p) => ({ ...p, overpass_city_limit: e.target.value }))} />
                </div>
                <div>
                  <Label>OSM radius (km)</Label>
                  <Input data-testid="ingest-overpass-radius" type="number" value={ingestForm.overpass_radius_km} onChange={(e) => setIngestForm((p) => ({ ...p, overpass_radius_km: e.target.value }))} />
                </div>
                <div>
                  <Label>OSM per-city limit</Label>
                  <Input data-testid="ingest-overpass-per-city-limit" type="number" value={ingestForm.overpass_per_city_limit} onChange={(e) => setIngestForm((p) => ({ ...p, overpass_per_city_limit: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <Label>OpenCorporates terms</Label>
                  <Input data-testid="ingest-opencorporates-terms" value={ingestForm.opencorporates_terms} onChange={(e) => setIngestForm((p) => ({ ...p, opencorporates_terms: e.target.value }))} />
                </div>
                <div>
                  <Label>OpenCorporates pages/term</Label>
                  <Input data-testid="ingest-opencorporates-pages" type="number" value={ingestForm.opencorporates_pages} onChange={(e) => setIngestForm((p) => ({ ...p, opencorporates_pages: e.target.value }))} />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button data-testid="ingest-run-button" onClick={runIngestion} disabled={ingestRunning}>
                  {ingestRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CloudDownload className="w-4 h-4 mr-2" />} Run Ingestion
                </Button>
                <Button data-testid="ingest-refresh-jobs" variant="outline" onClick={fetchIngestJobs}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh Jobs
                </Button>
                <Button data-testid="ingest-refresh-storage" variant="outline" onClick={fetchStorageSummary}>
                  <HardDrive className="w-4 h-4 mr-2" /> Refresh Storage
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card>
                  <CardHeader className="pb-2"><CardDescription>DB Records</CardDescription></CardHeader>
                  <CardContent data-testid="ingest-storage-db-records" className="text-xl font-semibold">{storageSummary?.database_records ?? '-'}</CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Ingest Jobs</CardDescription></CardHeader>
                  <CardContent data-testid="ingest-storage-job-count" className="text-xl font-semibold">{storageSummary?.ingest_jobs ?? '-'}</CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Snapshot Files</CardDescription></CardHeader>
                  <CardContent data-testid="ingest-storage-file-count" className="text-xl font-semibold">{storageSummary?.snapshot_files ?? '-'}</CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Snapshot Size (MB)</CardDescription></CardHeader>
                  <CardContent data-testid="ingest-storage-size-mb" className="text-xl font-semibold">{storageSummary?.snapshot_human_mb ?? '-'}</CardContent>
                </Card>
              </div>

              <div className="border rounded-lg p-4 space-y-4" data-testid="state-scrape-config-section">
                <div>
                  <h3 className="font-semibold">State-by-State Daily Scrape Plan</h3>
                  <p className="text-sm text-muted-foreground">5 states/day → target 10k/day, then next 5 states next day.</p>
                </div>

                {stateScrapeLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading config...</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          data-testid="state-scrape-enabled"
                          checked={!!stateScrapeConfig.enabled}
                          onCheckedChange={(checked) => setStateScrapeConfig((p) => ({ ...p, enabled: checked === true }))}
                        />
                        <Label>Enabled</Label>
                      </div>
                      <div>
                        <Label>States/day</Label>
                        <Input data-testid="state-scrape-states-per-day" type="number" value={stateScrapeConfig.states_per_day ?? 5} onChange={(e) => setStateScrapeConfig((p) => ({ ...p, states_per_day: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Target/state</Label>
                        <Input data-testid="state-scrape-target-per-state" type="number" value={stateScrapeConfig.target_per_state ?? 2000} onChange={(e) => setStateScrapeConfig((p) => ({ ...p, target_per_state: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Daily target</Label>
                        <Input data-testid="state-scrape-daily-target" type="number" value={stateScrapeConfig.daily_target ?? 10000} onChange={(e) => setStateScrapeConfig((p) => ({ ...p, daily_target: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Run hour (UTC)</Label>
                        <Input data-testid="state-scrape-run-hour" type="number" value={stateScrapeConfig.run_hour_utc ?? 3} onChange={(e) => setStateScrapeConfig((p) => ({ ...p, run_hour_utc: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Run minute (UTC)</Label>
                        <Input data-testid="state-scrape-run-minute" type="number" value={stateScrapeConfig.run_minute_utc ?? 0} onChange={(e) => setStateScrapeConfig((p) => ({ ...p, run_minute_utc: e.target.value }))} />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button data-testid="state-scrape-save-config" onClick={saveStateScrapeConfig} disabled={stateScrapeSaving}>
                        {stateScrapeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Schedule
                      </Button>
                      <Button data-testid="state-scrape-run-now" variant="outline" onClick={runStateDailyBatchNow} disabled={stateScrapeRunning}>
                        {stateScrapeRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CloudDownload className="w-4 h-4 mr-2" />} Run Daily Batch Now
                      </Button>
                    </div>

                    <div className="text-sm text-muted-foreground" data-testid="state-scrape-next-preview">
                      Next states: {(stateScrapeConfig.next_states_preview || []).join(', ') || '-'}
                      <br />Last run date: {stateScrapeConfig.last_run_date || '-'}
                    </div>
                  </>
                )}
              </div>

              <div className="border rounded-lg p-4 space-y-4" data-testid="tabular-import-section">
                <div>
                  <h3 className="font-semibold">Import CSV / Excel / Google Sheets</h3>
                  <p className="text-sm text-muted-foreground">Unknown columns auto-create top-level fields per custom entity.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Custom Entity Name</Label>
                    <Input data-testid="import-entity-name" value={importEntityName} onChange={(e) => setImportEntityName(e.target.value)} placeholder="ex: roofing_supplier_batch" />
                  </div>
                  <div>
                    <Label>Storage Mode</Label>
                    <Select value={importStorageMode} onValueChange={setImportStorageMode}>
                      <SelectTrigger data-testid="import-storage-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="database_only">Database Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Upload File</Label>
                    <Input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button data-testid="import-file-button" onClick={importFromFile} disabled={importingFile}>
                    {importingFile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />} Import File
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label>Google Sheets URL</Label>
                    <Input data-testid="import-sheet-url" value={importSheetUrl} onChange={(e) => setImportSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." />
                  </div>
                  <div className="flex items-end">
                    <Button data-testid="import-sheet-button" onClick={importFromGoogleSheet} disabled={importingSheet} className="w-full">
                      {importingSheet ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CloudDownload className="w-4 h-4 mr-2" />} Import Google Sheet
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Discovered Entity Schemas</Label>
                  <div className="border rounded-md overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Entity</TableHead>
                          <TableHead>Fields</TableHead>
                          <TableHead>Updated</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entitySchemas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground">No custom entity schema yet.</TableCell>
                          </TableRow>
                        ) : entitySchemas.slice(0, 20).map((entity) => (
                          <TableRow key={entity.id || entity.entity_name} data-testid={`entity-schema-row-${entity.entity_name}`}>
                            <TableCell>{entity.entity_name}</TableCell>
                            <TableCell className="text-xs">{(entity.fields || []).slice(0, 12).join(', ') || '-'}</TableCell>
                            <TableCell>{entity.updated_at ? new Date(entity.updated_at).toLocaleString() : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ingestion Jobs</CardTitle>
              <CardDescription>Track status, inserted/updated counts, and errors.</CardDescription>
            </CardHeader>
            <CardContent>
              {ingestJobsLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sources</TableHead>
                        <TableHead>Fetched</TableHead>
                        <TableHead>Inserted</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead>Errors</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ingestJobs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground">No ingestion jobs yet.</TableCell>
                        </TableRow>
                      ) : ingestJobs.map((job) => (
                        <TableRow key={job.id} data-testid={`ingest-job-row-${job.id}`}>
                          <TableCell className="font-mono text-xs">{job.id}</TableCell>
                          <TableCell><Badge variant={job.status === 'completed' ? 'default' : 'outline'}>{job.status}</Badge></TableCell>
                          <TableCell className="text-xs">{(job.sources || []).join(', ')}</TableCell>
                          <TableCell>{job.totals?.fetched ?? 0}</TableCell>
                          <TableCell>{job.totals?.inserted ?? 0}</TableCell>
                          <TableCell>{job.totals?.updated ?? 0}</TableCell>
                          <TableCell>{job.totals?.errors ?? 0}</TableCell>
                          <TableCell>{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Plug className="w-4 h-4" /> Provider Settings</CardTitle>
              <CardDescription>
                Start in free mode. Add Clearbit/ZoomInfo credentials later to enable live provider mode.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {providerLoading || !providerSettings ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      data-testid="business-free-mode-enabled"
                      checked={providerSettings.free_mode_enabled}
                      onCheckedChange={(checked) => setProviderSettings((prev) => ({ ...prev, free_mode_enabled: checked === true }))}
                    />
                    <Label>Enable Free Mode</Label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" /> Clearbit</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            data-testid="business-provider-clearbit-enabled"
                            checked={providerSettings.providers.clearbit.enabled}
                            onCheckedChange={(checked) => setProviderSettings((prev) => ({
                              ...prev,
                              providers: {
                                ...prev.providers,
                                clearbit: { ...prev.providers.clearbit, enabled: checked === true },
                              },
                            }))}
                          />
                          <Label>Enable Clearbit</Label>
                        </div>
                        <Input
                          data-testid="business-provider-clearbit-api-key"
                          placeholder="Clearbit API Key"
                          value={providerSettings.providers.clearbit.api_key || ''}
                          onChange={(e) => setProviderSettings((prev) => ({
                            ...prev,
                            providers: {
                              ...prev.providers,
                              clearbit: { ...prev.providers.clearbit, api_key: e.target.value },
                            },
                          }))}
                        />
                        <Badge variant="outline" data-testid="business-provider-clearbit-status">
                          {providerSettings.providers.clearbit.live_ready ? 'Live Ready' : 'Not Live'}
                        </Badge>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" /> ZoomInfo</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            data-testid="business-provider-zoominfo-enabled"
                            checked={providerSettings.providers.zoominfo.enabled}
                            onCheckedChange={(checked) => setProviderSettings((prev) => ({
                              ...prev,
                              providers: {
                                ...prev.providers,
                                zoominfo: { ...prev.providers.zoominfo, enabled: checked === true },
                              },
                            }))}
                          />
                          <Label>Enable ZoomInfo</Label>
                        </div>
                        <Input
                          data-testid="business-provider-zoominfo-api-key"
                          placeholder="ZoomInfo API Key"
                          value={providerSettings.providers.zoominfo.api_key || ''}
                          onChange={(e) => setProviderSettings((prev) => ({
                            ...prev,
                            providers: {
                              ...prev.providers,
                              zoominfo: { ...prev.providers.zoominfo, api_key: e.target.value },
                            },
                          }))}
                        />
                        <Input
                          data-testid="business-provider-zoominfo-username"
                          placeholder="ZoomInfo Username"
                          value={providerSettings.providers.zoominfo.username || ''}
                          onChange={(e) => setProviderSettings((prev) => ({
                            ...prev,
                            providers: {
                              ...prev.providers,
                              zoominfo: { ...prev.providers.zoominfo, username: e.target.value },
                            },
                          }))}
                        />
                        <Input
                          data-testid="business-provider-zoominfo-password"
                          type="password"
                          placeholder="ZoomInfo Password"
                          value={providerSettings.providers.zoominfo.password || ''}
                          onChange={(e) => setProviderSettings((prev) => ({
                            ...prev,
                            providers: {
                              ...prev.providers,
                              zoominfo: { ...prev.providers.zoominfo, password: e.target.value },
                            },
                          }))}
                        />
                        <Badge variant="outline" data-testid="business-provider-zoominfo-status">
                          {providerSettings.providers.zoominfo.live_ready ? 'Live Ready' : 'Not Live'}
                        </Badge>
                      </CardContent>
                    </Card>
                  </div>

                  <Button data-testid="business-save-provider-settings" onClick={updateProviderSettings} disabled={providerSaving}>
                    {providerSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Provider Settings
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <BusinessLeadDetailModal
        open={leadDetailOpen}
        onOpenChange={setLeadDetailOpen}
        lead={leadDetailData}
        loading={leadDetailLoading}
        sourceLabel={leadDetailSourceLabel}
      />
    </div>
  );
}
